/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, Fragment } from "react"
import { Col, Checkbox, Tag } from "@duxton-web/ui"
import { CheckboxChangeEvent } from "@duxton-web/ui/lib/checkbox"
import classnames from "classnames"
import { Item } from "types/models/menu"
import { Image } from "components/Image"
import useMenuContext from "hooks/useMenuContext"
import {
  isSpecialItem,
  isItemAvailable,
  AvailableStatusesText,
  availableStatusesUsage,
  availableStatusesStyle,
  useItemWithBackTimeMessageText,
  availableStatuses,
} from "utils/item_helper"
import { OverflowTip } from "components/common"
import dayjs from "dayjs"
import { useIsBcrsEnabled } from "pages/menu/detail/hooks/useIsBcrsEnabled"
import styles from "../MenuItemRow.modules.css"
import { useItemSelectionDrawerClick } from "./MenuRowSelectionControls"

// An item renders as unavailable when it is not sellable, or when it is a
// special (alcohol) item but the alcohol setting is turned off.
export const isItemUnavailable = (
  item: Item,
  enableAlcoholSetting?: boolean,
): boolean =>
  [!isItemAvailable(item), !enableAlcoholSetting && isSpecialItem(item)].some(
    val => val,
  )

// Highlights the search text in the item name; returns the text unchanged
// when there is nothing to highlight (V3 rows pass no search string).
const highlightText = (text: string, search?: string) => {
  if (!search || !search.trim()) {
    return text
  }

  const searchLower = search.toLowerCase()
  const textLower = text.toLowerCase()
  const index = textLower.indexOf(searchLower)

  if (index === -1) {
    return text
  }

  const before = text.substring(0, index)
  const match = text.substring(index, index + search.length)
  const after = text.substring(index + search.length)

  return (
    <Fragment>
      {before}
      <span className={styles.highlightText}>{match}</span>
      {after}
    </Fragment>
  )
}

interface IMenuItemRowColumnsProps {
  item: Item
  isUnavailable: boolean
  highlightString?: string
}

// Leading columns shared by MenuItemRow and MenuItemRowV3: item image, name,
// price, BCRS tag and the unavailable-status tag.
export const MenuItemRowColumns: FC<IMenuItemRowColumnsProps> = ({
  item,
  isUnavailable,
  highlightString,
}: IMenuItemRowColumnsProps) => {
  const { isServiceBasedMenuEnabled } = useMenuContext()
  const isBcrsEnabled = useIsBcrsEnabled()
  const availableUntilText = useItemWithBackTimeMessageText(
    dayjs(item.availableAt),
  )
  const availableStatusesText = AvailableStatusesText(
    availableStatusesUsage.MENU_TAG,
  )

  let tagText = ""
  if (
    item.availableStatus === availableStatuses.UNAVAILABLE_UNTIL_CUSTOM ||
    item.availableStatus === availableStatuses.UNAVAILABLE_UNTIL_NEXT_RESTOCK
  ) {
    tagText = availableUntilText
  } else {
    tagText = availableStatusesText[item.availableStatus as number]
  }

  return (
    <>
      <Col>
        <Image
          src={item.imageURL}
          width={73}
          height={73}
          opacity={isUnavailable ? 0.5 : 1}
        />
      </Col>
      <Col flex="auto">
        <OverflowTip
          className={classnames("name", isUnavailable && "unavailable")}
        >
          {highlightText(item.itemName || "", highlightString)}
        </OverflowTip>
        <OverflowTip
          className={classnames("price", isUnavailable && "unavailable")}
        >
          {isServiceBasedMenuEnabled ? item.priceRange : item.priceDisplay}
        </OverflowTip>
        {isBcrsEnabled && !!item?.bcrsInfo?.eligible && (
          <div
            data-testid={`item-bcrs-tag-${item.itemID}`}
            className={styles.bcrsTag}
          >
            BCRS
          </div>
        )}
      </Col>
      {isUnavailable && tagText && (
        <Col>
          <Tag
            size="xsmall"
            color={
              availableStatusesStyle[availableStatusesUsage.MENU_TAG][
                item.availableStatus as number
              ] ?? ""
            }
          >
            {tagText}
          </Tag>
        </Col>
      )}
    </>
  )
}

interface IMenuItemRowCheckboxProps {
  showSelection?: boolean
  selected: boolean
  onCheck: (value: boolean) => void
}

// Selection checkbox column shared by MenuItemRow and MenuItemRowV3.
export const MenuItemRowCheckbox: FC<IMenuItemRowCheckboxProps> = ({
  showSelection,
  selected,
  onCheck,
}: IMenuItemRowCheckboxProps) => {
  const handleClickSelected = useItemSelectionDrawerClick(showSelection)

  return (
    <Col className={`checkbox-container ${showSelection ? "show" : ""}`}>
      <Checkbox
        defaultChecked={false}
        checked={selected}
        onChange={(e: CheckboxChangeEvent) => {
          onCheck(e.target.checked)
        }}
        onClick={handleClickSelected}
      />
    </Col>
  )
}
